import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuditLogs, getAuditStats, getUsers } from '@/lib/api';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, Activity, Globe, Calendar, User, ChevronLeft, ChevronRight, 
  RefreshCw, Eye, FileText, Phone, Settings, Shield, Bell, Trash2,
  Play, Pause, Edit, Plus, Download, Upload, Filter, BarChart3,
  MousePointer, LogIn, LogOut, ClipboardList, Database, Clock,
  AlertTriangle, CheckCircle, XCircle, ExternalLink
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

// Action type configurations with icons and colors
const ACTION_CONFIG = {
  // Page views
  page_view: { label: 'Page View', icon: Eye, color: 'bg-blue-100 text-blue-700' },
  entity_view: { label: 'View Entity', icon: Eye, color: 'bg-blue-100 text-blue-700' },
  
  // Auth actions
  user_login: { label: 'Login', icon: LogIn, color: 'bg-green-100 text-green-700' },
  user_logout: { label: 'Logout', icon: LogOut, color: 'bg-muted text-slate-700' },
  
  // CRUD actions
  create: { label: 'Create', icon: Plus, color: 'bg-emerald-100 text-emerald-700' },
  update: { label: 'Update', icon: Edit, color: 'bg-amber-100 text-amber-700' },
  delete: { label: 'Delete', icon: Trash2, color: 'bg-red-100 text-red-700' },
  
  // Call actions
  viewed_calls_list: { label: 'Viewed Calls', icon: Phone, color: 'bg-blue-100 text-blue-700' },
  viewed_call_detail: { label: 'Viewed Call Detail', icon: Phone, color: 'bg-blue-100 text-blue-700' },
  created_call: { label: 'Created Call', icon: Phone, color: 'bg-green-100 text-green-700' },
  uploaded_call: { label: 'Uploaded Call', icon: Upload, color: 'bg-purple-100 text-purple-700' },
  assigned_call: { label: 'Assigned Call', icon: User, color: 'bg-amber-100 text-amber-700' },
  
  // User actions
  viewed_users_list: { label: 'Viewed Users', icon: User, color: 'bg-blue-100 text-blue-700' },
  viewed_user_profile: { label: 'Viewed User Profile', icon: User, color: 'bg-blue-100 text-blue-700' },
  created_user: { label: 'Created User', icon: User, color: 'bg-green-100 text-green-700' },
  updated_user: { label: 'Updated User', icon: User, color: 'bg-amber-100 text-amber-700' },
  deleted_user: { label: 'Deleted User', icon: User, color: 'bg-red-100 text-red-700' },
  
  // QA Notes
  created_qa_note: { label: 'Created QA Note', icon: ClipboardList, color: 'bg-purple-100 text-purple-700' },
  viewed_qa_notes: { label: 'Viewed QA Notes', icon: ClipboardList, color: 'bg-blue-100 text-blue-700' },
  
  // Processing
  started_onedrive_sync: { label: 'Started OneDrive Sync', icon: RefreshCw, color: 'bg-cyan-100 text-cyan-700' },
  started_call_processing: { label: 'Started Processing', icon: Play, color: 'bg-green-100 text-green-700' },
  stopped_call_processing: { label: 'Stopped Processing', icon: Pause, color: 'bg-red-100 text-red-700' },
  
  // Settings
  viewed_settings: { label: 'Viewed Settings', icon: Settings, color: 'bg-muted text-slate-700' },
  updated_settings: { label: 'Updated Settings', icon: Settings, color: 'bg-amber-100 text-amber-700' },
  
  // Master Delete
  viewed_master_stats: { label: 'Viewed Master Stats', icon: Database, color: 'bg-blue-100 text-blue-700' },
  executed_master_delete: { label: 'Master Delete', icon: Trash2, color: 'bg-red-100 text-red-700' },
  reset_all_stats: { label: 'Reset All Stats', icon: RefreshCw, color: 'bg-red-100 text-red-700' },
  
  // Notifications
  viewed_notifications: { label: 'Viewed Notifications', icon: Bell, color: 'bg-blue-100 text-blue-700' },
  marked_notification_read: { label: 'Marked Read', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  
  // Reports & Analytics
  viewed_reports: { label: 'Viewed Reports', icon: BarChart3, color: 'bg-blue-100 text-blue-700' },
  viewed_dashboard: { label: 'Viewed Dashboard', icon: BarChart3, color: 'bg-blue-100 text-blue-700' },
  viewed_team_hierarchy: { label: 'Viewed Team Hierarchy', icon: User, color: 'bg-blue-100 text-blue-700' },
  
  // UI Actions
  button_click: { label: 'Button Click', icon: MousePointer, color: 'bg-muted text-slate-700' },
  form_submit: { label: 'Form Submit', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  modal_open: { label: 'Modal Open', icon: ExternalLink, color: 'bg-muted text-slate-700' },
  search: { label: 'Search', icon: Search, color: 'bg-muted text-slate-700' },
  filter_change: { label: 'Filter Change', icon: Filter, color: 'bg-muted text-slate-700' },
  export: { label: 'Export', icon: Download, color: 'bg-purple-100 text-purple-700' },
  
  // Admin actions
  super_admin_entered_tenant: { label: 'Super Admin Access', icon: Shield, color: 'bg-red-100 text-red-700' },
  tenant_limits_updated: { label: 'Tenant Limits Updated', icon: Settings, color: 'bg-amber-100 text-amber-700' },
  trial_extended: { label: 'Trial Extended', icon: Clock, color: 'bg-green-100 text-green-700' },
  api_key_created: { label: 'API Key Created', icon: Plus, color: 'bg-green-100 text-green-700' },
  api_key_deleted: { label: 'API Key Deleted', icon: Trash2, color: 'bg-red-100 text-red-700' },
  api_key_regenerated: { label: 'API Key Regenerated', icon: RefreshCw, color: 'bg-amber-100 text-amber-700' },
  
  // Default
  default: { label: 'Action', icon: Activity, color: 'bg-muted text-slate-700' }
};

const ENTITY_TYPES = [
  { value: 'all', label: 'All Entities' },
  { value: 'page', label: 'Pages' },
  { value: 'call', label: 'Calls' },
  { value: 'user', label: 'Users' },
  { value: 'qa_note', label: 'QA Notes' },
  { value: 'notification', label: 'Notifications' },
  { value: 'settings', label: 'Settings' },
  { value: 'processing', label: 'Processing' },
  { value: 'master', label: 'Master Delete' },
  { value: 'onedrive', label: 'OneDrive' },
  { value: 'team', label: 'Team' },
];

export default function AuditTrail() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    user_id: 'all',
    action: 'all',
    entity_type: 'all',
    date_from: '',
    date_to: ''
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const pageSize = 50;
  
  // Detail dialog
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const params = {
        limit: pageSize,
        skip: page * pageSize
      };
      
      if (filters.user_id !== 'all') params.user_id = filters.user_id;
      if (filters.action !== 'all') params.action = filters.action;
      if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
      if (filters.date_from) params.date_from = filters.date_from + 'T00:00:00Z';
      if (filters.date_to) params.date_to = filters.date_to + 'T23:59:59Z';
      if (filters.search) params.action = filters.search; // Search in action names
      
      const { data } = await getAuditLogs(params);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error('Failed to load audit logs');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    setIsLoadingStats(true);
    try {
      const { data } = await getAuditStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Get dashboard users
      const { data: usersData } = await getUsers();
      setUsers(usersData || []);
      
      // Get tenant users (login users)
      const { data: tenantData } = await api.get('/tenant/users');
      setTenantUsers(tenantData.users || tenantData || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, filters.user_id, filters.action, filters.entity_type, filters.date_from, filters.date_to]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchLogs();
  };

  const getActionConfig = (action) => {
    return ACTION_CONFIG[action] || ACTION_CONFIG.default;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm:ss a');
    } catch {
      return dateStr;
    }
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  
  // Combine all users for filter dropdown
  const allUsers = [
    ...users.map(u => ({ id: u.id, name: u.name, type: 'user' })),
    ...tenantUsers.map(u => ({ id: u.id, name: u.name, type: 'tenant' }))
  ];

  // Get unique actions from logs for filter
  const uniqueActions = [...new Set(logs.map(l => l.action))].filter(Boolean);

  return (
    <div className="space-y-6" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="h-6 w-6 text-purple-500" />
            Audit Trail
          </h1>
          <p className="text-muted-foreground">Comprehensive tracking of all user activities and system events</p>
        </div>
        <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Events</p>
                <p className="text-2xl font-bold text-white">
                  {isLoadingStats ? <Skeleton className="h-8 w-16" /> : (stats?.total_logs || 0).toLocaleString()}
                </p>
              </div>
              <Activity className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Last 24 Hours</p>
                <p className="text-2xl font-bold text-green-500">
                  {isLoadingStats ? <Skeleton className="h-8 w-16" /> : (stats?.last_24_hours || 0).toLocaleString()}
                </p>
              </div>
              <Clock className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Users</p>
                <p className="text-2xl font-bold text-purple-500">
                  {isLoadingStats ? <Skeleton className="h-8 w-16" /> : stats?.users_by_activity?.length || 0}
                </p>
              </div>
              <User className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Action Types</p>
                <p className="text-2xl font-bold text-amber-500">
                  {isLoadingStats ? <Skeleton className="h-8 w-16" /> : stats?.actions_by_count?.length || 0}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Actions & Users (Quick Stats) */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="card-elevated">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Top Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.actions_by_count?.slice(0, 5).map((item, i) => {
                  const config = getActionConfig(item.action);
                  const Icon = config.icon;
                  return (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={config.color}>
                          <Icon className="h-3 w-3 mr-1" />
                          {config.label}
                        </Badge>
                      </div>
                      <span className="text-sm font-medium text-white">{item.count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-elevated">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Most Active Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.users_by_activity?.slice(0, 5).map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <User className="h-3 w-3 text-purple-400" />
                      </div>
                      <span className="text-sm text-white">{item.user_name || 'Unknown'}</span>
                    </div>
                    <span className="text-sm font-medium text-muted-foreground">{item.count} actions</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-muted-foreground mb-1 block">Search Actions</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by action name..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm text-muted-foreground mb-1 block">User</label>
              <Select value={filters.user_id} onValueChange={(v) => { setFilters({...filters, user_id: v}); setPage(0); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {allUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm text-muted-foreground mb-1 block">Entity Type</label>
              <Select value={filters.entity_type} onValueChange={(v) => { setFilters({...filters, entity_type: v}); setPage(0); }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[140px]">
              <label className="text-sm text-muted-foreground mb-1 block">From</label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => { setFilters({...filters, date_from: e.target.value}); setPage(0); }}
              />
            </div>
            
            <div className="w-[140px]">
              <label className="text-sm text-muted-foreground mb-1 block">To</label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => { setFilters({...filters, date_to: e.target.value}); setPage(0); }}
              />
            </div>
            
            <Button type="submit" variant="secondary">
              <Filter className="h-4 w-4 mr-2" />
              Apply
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Audit Events ({total.toLocaleString()})</CardTitle>
            <span className="text-sm text-muted-foreground">
              Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground">No audit events found</p>
              <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const config = getActionConfig(log.action);
                    const Icon = config.icon;
                    
                    return (
                      <TableRow 
                        key={log.id} 
                        className="cursor-pointer hover:bg-slate-800/50"
                        onClick={() => { setSelectedLog(log); setDetailOpen(true); }}
                      >
                        <TableCell>
                          <Badge className={config.color}>
                            <Icon className="h-3 w-3 mr-1" />
                            {config.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center">
                              <User className="h-3 w-3 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{log.user_name || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground">{log.user_role}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-muted-foreground">
                            {log.entity_type}
                          </Badge>
                          {log.entity_id && (
                            <p className="text-xs text-muted-foreground mt-1">{log.entity_id.slice(0, 12)}...</p>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.details?.page && (
                            <p className="text-sm text-muted-foreground truncate max-w-[200px]">{log.details.page}</p>
                          )}
                          {log.details?.button && (
                            <p className="text-sm text-muted-foreground">Button: {log.details.button}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm text-white">{formatRelativeTime(log.created_at)}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(log.created_at)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 p-4 border-t border-slate-700">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-500" />
              Audit Event Details
            </DialogTitle>
            <DialogDescription>
              Full details of this audit event
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              {/* Action Badge */}
              <div className="flex items-center gap-2">
                <Badge className={getActionConfig(selectedLog.action).color}>
                  {(() => { const Icon = getActionConfig(selectedLog.action).icon; return <Icon className="h-3 w-3 mr-1" />; })()}
                  {getActionConfig(selectedLog.action).label}
                </Badge>
                <Badge variant="outline">{selectedLog.entity_type}</Badge>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <div>
                    <p className="text-muted-foreground">User</p>
                    <p className="text-white font-medium">{selectedLog.user_name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">{selectedLog.user_id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Role</p>
                    <p className="text-white">{selectedLog.user_role || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Timestamp</p>
                    <p className="text-white">{formatDate(selectedLog.created_at)}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-muted-foreground">Entity ID</p>
                    <p className="text-white font-mono text-xs">{selectedLog.entity_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Source</p>
                    <p className="text-white">{selectedLog.source || 'backend'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Request</p>
                    <p className="text-white text-xs">{selectedLog.request_method} {selectedLog.request_path}</p>
                  </div>
                </div>
              </div>
              
              {/* Details JSON */}
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <p className="text-muted-foreground mb-2">Additional Details</p>
                  <pre className="bg-slate-800 rounded-lg p-3 text-xs text-muted-foreground/70 overflow-auto max-h-[200px]">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
